import { useState, useRef, FC } from 'react'
import { useHistory } from 'react-router-dom'

import { Overlay, Popover } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'

import { BACKEND_URL, graphqlEndpoint } from 'AppSettings'
import { useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import { getStatus } from 'components/atoms/CategoryDocumentList'
import { DocumentStatus, getDocumentStatus } from 'share/utils/documentUtils'
import ImageFallback from 'components/molecules/ImageFallback'

import DocumentsAddReplaceModal from '../DocumentsAddReplaceModal'
import './styles.scss'

function formatDate(
  dateStr: string | null | undefined
): string | null | undefined {
  const today = new Date()
  try {
    if (dateStr) {
      const dt = new Date(dateStr)
      if (
        today.getFullYear() === dt.getFullYear() &&
        today.getMonth() === dt.getMonth() &&
        today.getDate() === dt.getDate()
      ) {
        return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    }

    return dateStr
  } catch {
    return dateStr
  }
}

const DocumentDetail: FC = () => {
  const history = useHistory()
  const [show, setShow] = useState(false)
  const [showDocReplaceModal, setShowDocReplaceModal] = useState(false)
  const documentProcessing = useAppSelector((state) => state.documentProcessing)
  const authentication = useAppSelector((state) => state.authentication)

  const loadingDelay = useLoadingDelay(!documentProcessing)

  const ref = useRef(null)
  const refMore = useRef(null)

  const downloadDocument = (id: number, filename: string) => {
    const BackendURL = graphqlEndpoint.replace('/graphql', '')
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Authorization', `Bearer ${authentication.idToken}`)
    requestHeaders.set('Access_Token', `${authentication.accessToken}`)

    const options = {
      method: 'GET',
      headers: requestHeaders
    }

    fetch(`${BackendURL}api/file/download/${id}`, options)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)

        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        )

        document.body.removeChild(link)
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobUrl)
        }, 100)
      })
      .catch((err) => console.log(err))
  }

  const isReview = documentProcessing.selectedDocumentCategory === null
  const docStatus = getDocumentStatus(documentProcessing.selectedDocument)

  return (
    <>
      <GoBackHeader
        text={
          isReview
            ? 'Review Documents'
            : documentProcessing.selectedDocumentCategory?.name
        }
      />
      {loadingDelay && loadingSpinnerPortal()}
      <div className='ui-docs-detail-container'>
        <div className='ui-doc-details'>
          <div className='ui-docs-item-icon'>
            <span className='mdi mdi-file-pdf' />
          </div>
          <div className='ui-docs-item'>
            <p data-testid='document-detail-selected-doc-name'>
              {documentProcessing.selectedDocument?.name}
            </p>
            <span
              data-testid='document-detail-selected-doc-uploaded'
              className='ui-doc-upload'
            >
              Uploaded:
              {formatDate(documentProcessing.selectedDocument?.uploaded)}
            </span>
            {!isReview && (
              <div className='ui-docs-status'>
                <span
                  data-testid='document-detail-selected-doc-status'
                  className={getStatus(
                    documentProcessing.selectedDocument?.status
                  )}
                >
                  {documentProcessing.selectedDocument?.status}
                </span>
              </div>
            )}
          </div>
          <div className='ui-docs-button'>
            <span
              className='mdi mdi-dots-vertical'
              data-testid='document-detail-selected-doc-button'
              onClick={() => setShow(!show)}
              ref={refMore}
            />
            <Overlay
              show={show}
              target={refMore}
              placement='bottom'
              container={ref.current}
              containerPadding={20}
              rootClose={true}
              rootCloseEvent='mousedown'
              onHide={() => setShow(!show)}
            >
              <Popover id='more-options'>
                <Popover.Content style={{ padding: 0 }}>
                  <ul className='ui-doc-options'>
                    <li
                      data-testid='document-detail-selected-doc-download'
                      onClick={() => {
                        if (documentProcessing.selectedDocument?.uploaded)
                          downloadDocument(
                            documentProcessing.selectedDocument?.id,
                            documentProcessing.selectedDocument?.name
                          )
                      }}
                    >
                      Download
                    </li>
                    {docStatus === DocumentStatus.Pending && !isReview && (
                      <li
                        data-testid='document-detail-selected-doc-edit'
                        onClick={() =>
                          history.push('/dashboard/documents/edit')
                        }
                      >
                        Edit
                      </li>
                    )}
                  </ul>
                </Popover.Content>
              </Popover>
            </Overlay>
          </div>
        </div>
        <div className='ui-replace-button'>
          {docStatus === DocumentStatus.Rejected && (
            <Button
              data-testid='document-detail-replace-document-button'
              variant='primary'
              onClick={() => setShowDocReplaceModal(true)}
            >
              Replace Document
            </Button>
          )}
        </div>
        <div className='ui-doc-comments'>
          <div className='ui-doc-comments-text'>Document Comment(s)</div>
          {documentProcessing.selectedDocument &&
          documentProcessing.selectedDocument.comments &&
          documentProcessing.selectedDocument.comments.length > 0 ? (
            <ul className='mb-0'>
              <li
                key={
                  documentProcessing.selectedDocument.comments[
                    documentProcessing.selectedDocument?.comments.length - 1
                  ].id
                }
              >
                <div className='ui-comment-avatar'>
                  {documentProcessing.selectedDocument &&
                  documentProcessing.selectedDocument.comments &&
                  documentProcessing.selectedDocument.comments.length > 0 &&
                  documentProcessing.selectedDocument.comments[
                    documentProcessing.selectedDocument.comments.length - 1
                  ] &&
                  documentProcessing.selectedDocument.comments[
                    documentProcessing.selectedDocument.comments.length - 1
                  ].attorneyAvatarUrl ? (
                    <ImageFallback
                      src={`${BACKEND_URL}${
                        documentProcessing.selectedDocument.comments[
                          documentProcessing.selectedDocument?.comments.length -
                            1
                        ].attorneyAvatarUrl as string
                      }`}
                      alt='Attorney avatar'
                      fallback={<span className='mdi mdi-account-circle' />}
                    />
                  ) : (
                    <span className='mdi mdi-account-circle' />
                  )}
                </div>
                <div>
                  <p>
                    {
                      documentProcessing.selectedDocument.comments[
                        documentProcessing.selectedDocument?.comments.length - 1
                      ].attorneyName
                    }
                  </p>
                  {
                    documentProcessing.selectedDocument.comments[
                      documentProcessing.selectedDocument?.comments.length - 1
                    ].comment
                  }
                  <span>
                    {formatDate(
                      documentProcessing.selectedDocument.comments[
                        documentProcessing.selectedDocument?.comments.length - 1
                      ].created
                    )}
                  </span>
                </div>
              </li>
            </ul>
          ) : (
            <div className='ui-no-comments-container'>
              <div className='ui-no-comments-icon'>
                <span className='mdi mdi-comment-text-outline' />
              </div>
              <span className='ui-no-comments-text'>
                Your attorney did not include any comments about this document.
              </span>
            </div>
          )}
        </div>
      </div>
      {docStatus === DocumentStatus.Rejected && (
        <DocumentsAddReplaceModal
          title='Replace Document'
          show={showDocReplaceModal}
          onHide={() => setShowDocReplaceModal(false)}
          replacedDocumentId={
            documentProcessing.selectedDocument?.id
              ? documentProcessing.selectedDocument?.id
              : null
          }
          businessObjectId={null}
          businessObjectType={''}
          folderName={documentProcessing?.selectedDocumentCategory?.name ?? ''}
          folderDescription={
            documentProcessing?.selectedDocumentCategory?.description ?? ''
          }
          isAiCase={false}
        />
      )}
    </>
  )
}

export default DocumentDetail
